// UI-weite Einstellungen: Icon-Größe (Glove-Mode), Satelliten-Style-Toggle,
// weitere globale UX-Präferenzen. In usePreference (localStorage) persistiert
// und über 'adt:pref-change' synct.
//
// Warum als eigene Datei: die UI-Settings wachsen, und useRideSettings ist
// thematisch anders (Ride/Explore-Zoom). Klare Trennung, klarere Imports.

import { useEffect } from 'react';
import { DEFAULT_REGION, isRegionId, setActiveColorMode, type RegionId } from '@adtu/shared';
import { useBoolPreference, useNumberPreference, usePreference } from './usePreference';

// ---------------------------------------------------------------------------
// Icon-Size / Glove-Mode
// ---------------------------------------------------------------------------
//
// User-Wunsch: "mach im profile das auswählbar (icons grösse, glove mode):
// small. medium . large. small so wie jetzt. medium gut 1,5x grösser, large
// 2x grösser und durchsichtiger damit man noch was drunter erkennt."
//
// Wir wenden den Skalier-Faktor als CSS-Var auf :root an (siehe index.css).
// Konsument-Klassen lesen die Var in transform:scale() + opacity, damit wir
// Text-Layouts NICHT kaputt machen (nur die Overlays skalieren).

// Wave 14ca-fix64: 3-Stufen (small/medium/large) → 2-Stufen
// (normal/gloves). User-Wunsch: "mir reichen 2 Stufen, statt small medium
// und large nenne es normal und gloves".
//
// Migration: bestehende User mit Pref-Werten 'small'|'medium'|'large' werden
// in useIconSize() gemapped (s. unten). Neue Werte landen ausschliesslich
// als 'normal'|'gloves' in localStorage.
export type IconSize = 'normal' | 'gloves';

export const PREF_ICON_SIZE = 'adt_icon_size';
export const DEFAULT_ICON_SIZE: IconSize = 'normal';

const ICON_SIZE_VALUES: readonly IconSize[] = ['normal', 'gloves'];

/**
 * Wave 14dx: Glove-Mode REAKTIVIERT (Variante 2).
 *
 * User-Feedback Mai 2026: "wir können uns wieder an den GLOVE MODE
 * versuchen, der gerade deaktiviert ist, er sollte nur markierte (blau
 * eingekreist) buttons grösser machen, erstmal 30-50%, übertreib nicht".
 *
 * Markiert wurden:
 *   - oben rechts: Locate + CompassRose  → PRIMARY  (--icon-scale)
 *   - rechts mittig: Zoom +/- + Zoom-Anzeige + FullscreenToggle
 *                    → TERTIARY (--icon-scale-tertiary)
 *
 * NICHT markiert: Style-Switcher oben links → SECONDARY bleibt bei 1.0
 * (die alte Variante hat den auch mitgezogen und das hat zu Layout-
 * Überlappungen geführt).
 *
 * Skalierung: 1.4 (≈ 40% groesser) — mittig im vom User gewuenschten
 * 30–50% Korridor, ohne den Touch-Target weiter aufzublasen.
 * Opacity 0.95 + Chrome-Alpha runter, damit die groesseren Pills die
 * Karte nicht zudecken.
 */

/** Skalierungsfaktor pro Stufe — Primary (Locate + Compass top-right).
 *  Wave 14em-fix2: zurueck auf 1.0. Egal welchen Wert ich gewaehlt habe
 *  (1.25, 1.35, 1.4) — der vergroesserte Compass kollidiert visuell mit
 *  dem darunterliegenden Locate-Button (MapLibre's ctrl-top-right rendert
 *  beide ohne ordentlich skalierenden Gap). User-Feedback: "ist wieder
 *  hinter der kompass rose!!!!!". Statt weiter zu raten: top-right
 *  bleibt normal-gross, der wichtigere Stack (Zoom + Fullscreen unten)
 *  bekommt den Glove-Boost via TERTIARY.
 */
export const ICON_SCALE: Record<IconSize, number> = {
  normal: 1,
  gloves: 1,
};

export const ICON_OPACITY: Record<IconSize, number> = {
  normal: 1,
  gloves: 1,
};

/** Style-Switcher oben links — der User hat ihn NICHT markiert, also bei 1.0. */
export const ICON_SCALE_SECONDARY: Record<IconSize, number> = {
  normal: 1,
  gloves: 1,
};

export const ICON_OPACITY_SECONDARY: Record<IconSize, number> = {
  normal: 1,
  gloves: 1,
};

/** Zoom + Fullscreen unten rechts — vom User markiert, gleicher Scale wie Primary.
 *  Wave 14em-fix: 1.25 → 1.35 (s. ICON_SCALE). */
export const ICON_SCALE_TERTIARY: Record<IconSize, number> = {
  normal: 1,
  gloves: 1.35,
};

export const ICON_OPACITY_TERTIARY: Record<IconSize, number> = {
  normal: 1.0,
  gloves: 1.0,
};

/**
 * CHROME-Alpha — Hintergrund- und Border-Transparenz der Map-Overlay-Pills.
 *
 * User-Wunsch 14l: "ab mittlerer stufe mach die logos oben rechts viel
 * durchsichtiger, zumindest die schwarze fläche, ich will trails darunter
 * erahnen können. ab gross auch links die für die 3 kartentypen."
 *
 * Warum getrennt von ICON_OPACITY: wenn wir die ganze Pill transparent machen
 * würden (inkl. Icons), wären die Icons kaum mehr erkennbar. Wir wollen NUR
 * das dunkle Chrome (background + border) verblassen lassen, damit man die
 * Trails darunter sieht — die Icons selbst bleiben voll opak, damit man weiß
 * welchen Button man trifft.
 *
 * Primary = Controls die man oft bei Fahrt klickt (oben rechts: Locate,
 * Compass). Hier schlägt der Transparenz-Drop schon bei Medium zu, weil die
 * Buttons zwar groß genug sind (Glove-Scale) aber die schwarze Fläche
 * unnötig viel Map verdeckt.
 *
 * Secondary = Controls die man selten klickt (oben links: Style-Switcher).
 * Hier ist der Glove-Scale bereits eine Stufe kleiner (ICON_SCALE_SECONDARY)
 * und das Chrome-Fade tritt entsprechend eine Stufe später ein — erst bei
 * Large.
 */
/** CHROME-Alpha — Primary bleibt jetzt unveraendert (ICON_SCALE = 1.0
 *  auch bei gloves, Wave 14em-fix2). */
export const CHROME_BG_ALPHA_PRIMARY: Record<IconSize, number> = {
  normal: 0.85,
  gloves: 0.85,
};
export const CHROME_BORDER_ALPHA_PRIMARY: Record<IconSize, number> = {
  normal: 0.5,
  gloves: 0.5,
};
export const CHROME_BG_ALPHA_SECONDARY: Record<IconSize, number> = {
  normal: 0.85,
  gloves: 0.85,
};
export const CHROME_BORDER_ALPHA_SECONDARY: Record<IconSize, number> = {
  normal: 0.5,
  gloves: 0.5,
};

/** Blur-Radius der Pill-Chrome — Primary bleibt unveraendert. */
export const CHROME_BLUR_PRIMARY: Record<IconSize, string> = {
  normal: '8px',
  gloves: '8px',
};
export const CHROME_BLUR_SECONDARY: Record<IconSize, string> = {
  normal: '8px',
  gloves: '8px',
};

/**
 * Wave 14ca-fix64: Migration alter Pref-Werte (small/medium/large) → neue
 * 2-Stufen-Skala (normal/gloves). small → normal, medium/large → gloves.
 * Unbekannte Werte fallen auf Default zurueck.
 */
function migrateLegacyIconSize(raw: string | null | undefined): IconSize {
  if (raw === 'normal' || raw === 'gloves') return raw;
  if (raw === 'small') return 'normal';
  if (raw === 'medium' || raw === 'large') return 'gloves';
  return DEFAULT_ICON_SIZE;
}

export function useIconSize(): [IconSize, (v: IconSize) => void] {
  const [raw, setRaw] = usePreference(PREF_ICON_SIZE, DEFAULT_ICON_SIZE);
  const safe = migrateLegacyIconSize(raw);
  return [safe, (v) => setRaw(v)];
}

// ---------------------------------------------------------------------------
// Satellite-Style-Toggle
// ---------------------------------------------------------------------------
//
// Historie:
//   • Initial: satellite AN per default.
//   • Batch 12: User wollte satellite weg → default AUS, optional im Profile.
//   • Wave 14k: User will den Satellite-Style zurück als Default im neuen
//     Segmented-Toggle ("satellit sieht super cool aus, mach es doch default
//     an"). Der Style selbst bleibt weiterhin 'mapout' als Default-Karte —
//     hier geht's nur darum, ob der Satellite-Knopf im Style-Switcher
//     sichtbar ist. Wer ihn nicht mag, kann ihn im Profile weiter abschalten;
//     der MapPage-Guard (siehe useSatelliteEnabled-Effekt in MapPage) fällt
//     dann automatisch auf 'mapout' zurück, falls der User grad drin war.

export const PREF_SATELLITE_ENABLED = 'adt_satellite_enabled';
export const DEFAULT_SATELLITE_ENABLED = true;

export function useSatelliteEnabled(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_SATELLITE_ENABLED, DEFAULT_SATELLITE_ENABLED);
}

// ---------------------------------------------------------------------------
// Hillshade-Toggle (Akku-Optimierung)
// ---------------------------------------------------------------------------
//
// Wave 14bp (Energiespar-Folgewelle): der Hillshade-Layer wird NUR im Ride-
// Mode und im 'heading'-Compass-Mode automatisch ausgeblendet (siehe
// MapPage.hillshadeVisible). Im Explore-Mode bleibt er standardmäßig AN.
// Problem: der User fährt de-facto meistens im Explore-Mode Rad, und dabei
// frisst der illumination-anchor:'viewport'-Shader pro Bearing-Änderung
// die GPU.
//
// Lösung: eine Master-Präferenz "Hillshade". Default AN (damit sich visuell
// nix ändert), wer Akku sparen will schaltet's in Profile aus. Die existing
// Ride/Heading-Auto-Off-Logik bleibt bestehen — dieser Toggle ist ein harter
// Overlay-Off, unabhängig vom Mode.
export const PREF_SHOW_HILLSHADE = 'adt_show_hillshade';
export const DEFAULT_SHOW_HILLSHADE = true;

export function useShowHillshade(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_SHOW_HILLSHADE, DEFAULT_SHOW_HILLSHADE);
}

// ---------------------------------------------------------------------------
// Schlüsselstellen / Crux-Points Toggle
// ---------------------------------------------------------------------------
//
// User-Wunsch Batch 7: "schlüsselstellen sehen […] aktivierbar rechts neben
// navigate to start. default an". Admin markiert pro Trail ≥0 Distance-Punkte,
// der Rider kann sie in Explore- und Ride-Ansicht ein-/ausblenden.
//
// Default AN — der User wollte explizit "bei der abfahrt drauf vorbereitet"
// sein, also zeigen wir's von Anfang an. Wer's übersichtlich mag schaltet aus.

export const PREF_SHOW_CRUX = 'adt_show_crux';
export const DEFAULT_SHOW_CRUX = true;

export function useShowCruxPoints(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_SHOW_CRUX, DEFAULT_SHOW_CRUX);
}

// ---------------------------------------------------------------------------
// Admin-Mode (On-Device)
// ---------------------------------------------------------------------------
//
// User-Wunsch Batch 7: "wenn ich mit dem handy unterwegs bin, mit der app,
// sollte ich auch als admin im trail anhalten können und die aktuelle location
// als crux zum trail hinzufügen können." Statt den User jedes Mal zurück in
// die eigene Admin-SPA (/admin/) zu schicken, spendieren wir einen eingebauten
// Admin-Mode im Rider-PWA: Toggle + Token. Ist der Toggle an UND ein Token
// gesetzt, blenden wir im Trail-Sheet einen "Mark crux here"-Button ein, der
// die aktuelle GPS-Position (als alongTrackMeters) über PATCH /api/admin/trails
// an den Server schickt.
//
// Token-Storage bewusst in localStorage (NICHT sessionStorage wie im Admin-UI)
// — der User ist auf'm Bike mit dem Handy unterwegs, die PWA kann beim Trail-
// head nach einem Screenlock neu laden; ein Token-Verlust bei jedem App-Reload
// wäre im Feld untragbar. Sicherheit: der Token ist ohnehin nur Schutz gegen
// "jemand anders findet das Handy" — im fremden Hand läuft dann auch ein
// Rider-PWA-Token, das's nur genau einen Angriffspfad (Trail-Edits) öffnet.
//
// Der Token ist separat von `adt_admin_token` (Admin-SPA, sessionStorage) —
// beide können unabhängig gesetzt sein. Wenn der User beides nutzt, muss er
// den Token zwei Mal einfügen; akzeptabel, da der Use-Case eher "dauerhaft
// on-device als Admin unterwegs" ist.

export const PREF_ADMIN_MODE = 'adt_admin_mode';
export const DEFAULT_ADMIN_MODE = false;

/** Admin-Mode-Toggle im Profile — schaltet den "Mark crux here"-Button auf der Karte frei. */
export function useAdminMode(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_ADMIN_MODE, DEFAULT_ADMIN_MODE);
}

export const PREF_ADMIN_TOKEN = 'adt_rider_admin_token';
export const DEFAULT_ADMIN_TOKEN = '';

/** Rider-PWA-seitiger Admin-Token (Bearer für /api/admin/…). Leerer String = unset. */
export function useAdminToken(): [string, (v: string) => void] {
  return usePreference(PREF_ADMIN_TOKEN, DEFAULT_ADMIN_TOKEN);
}

// Wave 14ca-fix58: Discovery-Mode-Toggle. Aktiviert die Block / One-way /
// Approve Buttons waehrend Recording, plus auto-approve aller traversierten
// OSM-Ways auf Ride-Stop. Setzt Admin-Mode + role admin/admin_light voraus.
export const PREF_DISCOVERY_MODE = 'adt_rider_discovery_mode';
export const DEFAULT_DISCOVERY_MODE = false;
export function useDiscoveryMode(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_DISCOVERY_MODE, DEFAULT_DISCOVERY_MODE);
}

// Wave 14ca-fix64: Rain-Mode-Feature-Gate.
// Admin-Toggle. Wenn ON: 🌧️-Button im Zoom-Stack sichtbar → User kann
// den Display-Lock aktivieren. Wenn OFF: Button wird nicht gerendert
// (Default fuer normale User). RainModeOverlay-Logik selbst bleibt
// immer geladen, nur der Aktivierungs-Knopf ist gated — so kann der
// Admin das Feature komplett verstecken bis er es freigibt.
export const PREF_RAIN_MODE_ENABLED = 'adt_rain_mode_enabled';
export const DEFAULT_RAIN_MODE_ENABLED = false;
export function useRainModeEnabled(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_RAIN_MODE_ENABLED, DEFAULT_RAIN_MODE_ENABLED);
}

// POI-Vektor-Tile-Rendering (drinking_water + fast_food direkt aus dem
// OpenMapTiles-`poi`-Source-Layer statt aus dem Server-Blob-Download).
// Siehe docs/poi-vector-tile-migration.md + lib/map/vectorPoiLayer.ts.
//
// Default OFF (Hotfix im Rennen): installierte PWAs lesen die Tiles STRIKT aus
// OPFS und haben dort noch die alten z13-Tiles gecacht — der poi-Layer enthält
// dort KEINE drinking_water/fast_food (die kommen erst mit den z14-Tiles). Bei
// default-ON wurde der Blob-OSM-Pfad ausgeblendet → gar keine Wasser-POIs mehr.
// Bis der Client die z14-Tiles neu heruntergeladen hat (OfflinePage), bleibt der
// zuverlässige Blob-Pfad die Default-Quelle. Vektor kann pro Client manuell
// aktiviert werden, wenn dessen OPFS die z14-Tiles hat.
export const PREF_VECTOR_POI_ENABLED = 'adt_vector_poi_enabled';
export const DEFAULT_VECTOR_POI_ENABLED = false;
export function useVectorPoiEnabled(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_VECTOR_POI_ENABLED, DEFAULT_VECTOR_POI_ENABLED);
}

// Wave 14ca-fix64: Pocket-Mode-Feature-Gate (OLED-Schwarz fuer Energie-
// Sparen waehrend Hosentaschen-Recording). Analog zu Rain-Mode: Admin-
// Toggle. Wenn ON: 🔋-Button im Zoom-Stack. Default OFF.
export const PREF_POCKET_MODE_ENABLED = 'adt_pocket_mode_enabled';
export const DEFAULT_POCKET_MODE_ENABLED = false;
export function usePocketModeEnabled(): [boolean, (v: boolean) => void] {
  return useBoolPreference(
    PREF_POCKET_MODE_ENABLED,
    DEFAULT_POCKET_MODE_ENABLED,
  );
}

// Wave 14ew: Höhenprofil-Zoom-Stufen (zwei Werte in Metern). Doppelklick im
// Profil cycelt full → near → far. Default 1000/5000. Als JSON-Tuple
// [near, far] in localStorage. Setter clampt in [MIN,MAX] und erzwingt
// near < far (mind. 100m Abstand), damit die Stufen nie vertauscht sind.
export const PREF_ELEVATION_ZOOM_LEVELS = 'adt_elevation_zoom_levels';
export const DEFAULT_ELEVATION_ZOOM_LEVELS: [number, number] = [1000, 5000];
export const ELEVATION_ZOOM_MIN = 200;
export const ELEVATION_ZOOM_MAX = 20000;

export function useElevationZoomLevels(): [
  [number, number],
  (v: [number, number]) => void,
] {
  const [raw, setRaw] = usePreference(
    PREF_ELEVATION_ZOOM_LEVELS,
    JSON.stringify(DEFAULT_ELEVATION_ZOOM_LEVELS),
  );
  let parsed: [number, number] = DEFAULT_ELEVATION_ZOOM_LEVELS;
  try {
    const arr = JSON.parse(raw);
    if (
      Array.isArray(arr) &&
      arr.length === 2 &&
      Number.isFinite(arr[0]) &&
      Number.isFinite(arr[1])
    ) {
      parsed = [arr[0], arr[1]];
    }
  } catch {
    /* kaputtes JSON → Default */
  }
  const setLevels = (v: [number, number]) => {
    const clamp = (n: number) =>
      Math.min(ELEVATION_ZOOM_MAX, Math.max(ELEVATION_ZOOM_MIN, Math.round(n)));
    let near = clamp(v[0]);
    let far = clamp(v[1]);
    if (near >= far) far = Math.min(ELEVATION_ZOOM_MAX, near + 100);
    if (near >= far) near = Math.max(ELEVATION_ZOOM_MIN, far - 100);
    setRaw(JSON.stringify([near, far]));
  };
  return [parsed, setLevels];
}

// Wave 14ds / 14dt: Battery-Safe-Mode Briefmarken-Größe. Fünf Stufen (Slider),
// alle 5:7-Ratio. Kleiner = mehr Schwarzfläche = mehr OLED-Ersparnis, größer =
// besser lesbar. `top` = Abstand vom oberen Rand (skaliert mit der Höhe, damit
// große Stufen nicht an der Statusleiste kleben). xl ist 2× das alte "large".
export type BatterySafeSize = 'xs' | 's' | 'm' | 'l' | 'xl';

export const BATTERY_SAFE_SIZES: readonly BatterySafeSize[] = [
  'xs',
  's',
  'm',
  'l',
  'xl',
];

export const BATTERY_SAFE_PRESETS: Record<
  BatterySafeSize,
  { w: number; h: number; top: number; label: string }
> = {
  xs: { w: 80, h: 112, top: 70, label: 'Extra small' },
  s: { w: 120, h: 168, top: 80, label: 'Small' },
  m: { w: 160, h: 224, top: 90, label: 'Medium' },
  l: { w: 220, h: 308, top: 110, label: 'Large' },
  xl: { w: 280, h: 392, top: 130, label: 'Extra large' },
};

export const PREF_BATTERY_SAFE_SIZE = 'adt_battery_safe_size';
export const DEFAULT_BATTERY_SAFE_SIZE: BatterySafeSize = 'm';

// Migration der alten 3-Stufen-Werte (small/medium/large) auf die neue
// 5-Stufen-Skala. small→xs, medium→m, large→l. Unbekanntes → Default.
function migrateBatterySafeSize(
  raw: string | null | undefined,
): BatterySafeSize {
  switch (raw) {
    case 'xs':
    case 's':
    case 'm':
    case 'l':
    case 'xl':
      return raw;
    case 'small':
      return 'xs';
    case 'medium':
      return 'm';
    case 'large':
      return 'l';
    default:
      return DEFAULT_BATTERY_SAFE_SIZE;
  }
}

export function useBatterySafeSize(): [BatterySafeSize, (v: BatterySafeSize) => void] {
  const [raw, setRaw] = usePreference(
    PREF_BATTERY_SAFE_SIZE,
    DEFAULT_BATTERY_SAFE_SIZE,
  );
  return [migrateBatterySafeSize(raw), (v) => setRaw(v)];
}

// Wave 14ca-fix64: Color-Assist-Mode (Rot-Gruen-Schwaeche).
// Schaltet die DIFFICULTY_COLOR-Palette (shared/types.ts) in eine
// CB-friendly Variante um — Easy wechselt von gruen zu cyan, Hard +
// intermediate_plus von rot zu vermillion. User-Wunsch: Admin-Toggle
// damit Riders mit rot-gruen-Schwaeche die gruenen Trails auf der
// gruenen Karte erkennen.
//
// Implementations-Detail: Hook setzt das Modul-Flag in shared/types.ts
// (setActiveColorMode) bei jedem Re-Render. Komponenten die DIFFICULTY_COLOR
// rendern muessen den Hook halten, damit der Re-Render bei Toggle-Aenderung
// triggert.
export const PREF_COLOR_ASSIST_MODE = 'adt_color_assist_mode';
export const DEFAULT_COLOR_ASSIST_MODE = false;
export function useColorAssistMode(): [boolean, (v: boolean) => void] {
  const [enabled, setEnabled] = useBoolPreference(
    PREF_COLOR_ASSIST_MODE,
    DEFAULT_COLOR_ASSIST_MODE,
  );
  // Side-effect: das Modul-Flag in shared/types.ts auf den aktuellen Wert
  // syncen, damit DIFFICULTY_COLOR-Lookups in der gleichen Render-Pass
  // bereits die richtige Palette liefern. useEffect feuert nach dem Render,
  // aber das Flag wird beim naechsten Render wirksam — und Komponenten
  // die diesen Hook halten re-rendern automatisch via useBoolPreference.
  useEffect(() => {
    setActiveColorMode(enabled ? 'cb' : 'standard');
  }, [enabled]);
  // Zusaetzlich synchron im Render-Body setzen: garantiert dass die ALLERSTE
  // Render-Pass nach Mount auch schon korrekte Farben hat (sonst flackern
  // Trails einmal in Standard, weil setActiveColorMode erst im
  // nachgeschalteten useEffect lauft).
  setActiveColorMode(enabled ? 'cb' : 'standard');
  return [enabled, setEnabled];
}

// ---------------------------------------------------------------------------
// Active Region (Liguria / Elba)
// ---------------------------------------------------------------------------
//
// Wave 14cf: User wählt bei der Erst-Installation entweder Liguria oder
// Elba als Heimat-Region. Die Region steuert:
//   - Initial-Camera-Bounds der Map (LIGURIA_BOUNDS vs ELBA_BOUNDS)
//   - Welche PMTiles-Files das FirstLaunchGate herunterlädt
//   - Welche Routing-DB der Backend für finger-/pin-route nutzt
//     (navi-liguria.sqlite vs navi-elba.sqlite)
//   - Welche Trails in der Liste auftauchen (nach Region gefiltert)
//
// Im OfflinePage kann der User später die andere Region NACHLADEN. Der
// activeRegion-Wert hier sagt nur welches "primäre" Setup gilt; das
// Vorhandensein der zweiten Region offline ist davon unabhängig — wer
// Elba als Default hat aber Liguria offline geladen hat, kann jederzeit
// nach Liguria switchen ohne Online-Zwang.
//
// Wir nutzen direkt den shared `RegionId`-Typ und validieren beim Lesen
// damit alte localStorage-Werte (oder manipulierte) sauber auf
// DEFAULT_REGION fallen.
// ---------------------------------------------------------------------------
// Ride-Next-Aura (Wave 14cf)
// ---------------------------------------------------------------------------
//
// Auf komplexen Routen (Loops, Trail-Cluster die mehrfach denselben Punkt
// kreuzen) ist während des Rides nicht klar, in welche Richtung's weitergeht.
// Lösung: die nächsten N Meter ab GPS-Position entlang der aktiven Route
// werden mit einer orange Aura hervorgehoben — der User sieht sofort wo's
// langgeht.
//
// 0 = Feature aus (kein Overlay). Default 500m, im Profile als Slider
// einstellbar (0-1500m, 100m-Schritte).
export const PREF_RIDE_NEXT_AURA_M = 'adt_ride_next_aura_m';
export const DEFAULT_RIDE_NEXT_AURA_M = 500;
export const RIDE_NEXT_AURA_MIN = 0;
export const RIDE_NEXT_AURA_MAX = 1500;
export const RIDE_NEXT_AURA_STEP = 100;

export function useRideNextAuraMeters(): [number, (v: number) => void] {
  return useNumberPreference(PREF_RIDE_NEXT_AURA_M, DEFAULT_RIDE_NEXT_AURA_M);
}

export const PREF_ACTIVE_REGION = 'adt_active_region';

/**
 * Liest die aktiv gewählte Region aus localStorage und gibt einen Setter
 * zurück. Wenn noch nichts gesetzt ist (frischer Erst-Install), lautet der
 * Wert DEFAULT_REGION ('liguria') — der Region-Picker im FirstLaunchGate
 * unterscheidet "noch nie gesetzt" via hasActiveRegionBeenChosen() (s.u.).
 */
/**
 * Corridor-Selfservice: neben den statisch bekannten RegionIds sind jetzt
 * auch dynamische Korridor-Slugs (Server-Manifest regions[], z. B.
 * 'andorra-meer-2') gültige aktive Regionen. Sie sind zur Compile-Zeit
 * nicht in der RegionId-Union — wir validieren das FORMAT (Slug wie in
 * backend/routes/corridors.js) und casten. Alle Konsumenten behandeln
 * unbekannte IDs defensiv (REGIONS[x]?.…, getRegionMapData-Fallback,
 * Manifest-Lookup per String-Vergleich), der Server validiert Routing-
 * Regionen selbst nochmal.
 */
/**
 * Wave 14ed — EINMAL-MIGRATION fuer Bestands-Installationen.
 *
 * Bis Wave 14ed gab es genau eine fest eingebaute Region: '3pbr'
 * ("3 Peaks Route"). Sie stand als DEFAULT_REGION in @adtu/shared und
 * landete damit bei JEDER Installation in localStorage — ohne dass der
 * User sie je gewaehlt haette. Jetzt ist die statische Registry leer.
 *
 * Ohne diese Migration bleibt der alte Wert '3pbr' liegen: er ist kein
 * isRegionId() mehr, matcht aber das Slug-Format, wird also als
 * "dynamischer Korridor" durchgewunken. Ergebnis waere genau das, was der
 * User NICHT will — ein Chip "3pbr" im Profil, der zu nichts gehoert und
 * den man nicht wegbekommt, weil es keinen Korridor-Job dahinter gibt.
 *
 * Wir loeschen den Wert deshalb einmalig. Danach steht der Bestands-User
 * wie ein Neu-User da: keine Region, kein Download, und er waehlt im
 * Profil seinen eigenen Korridor. Nur exakt dieser eine Legacy-Slug wird
 * angefasst — selbst gebaute Korridor-Slugs bleiben unberuehrt.
 */
const LEGACY_STATIC_REGION_SLUGS = new Set(['3pbr']);

export function migrateLegacyStaticRegion(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(PREF_ACTIVE_REGION);
    if (!raw) return;
    // Falls die Region jemals wieder statisch existiert: Finger weg.
    if (isRegionId(raw)) return;
    if (!LEGACY_STATIC_REGION_SLUGS.has(raw)) return;
    window.localStorage.removeItem(PREF_ACTIVE_REGION);
  } catch {
    /* localStorage nicht verfuegbar (Private Mode) — dann eben nicht. */
  }
}

export function isDynamicRegionSlug(raw: string | null | undefined): boolean {
  return typeof raw === 'string' && /^[a-z0-9][a-z0-9-]{1,40}$/.test(raw);
}

export function useActiveRegion(): [RegionId, (v: RegionId) => void] {
  const [raw, setRaw] = usePreference(PREF_ACTIVE_REGION, DEFAULT_REGION);
  const safe: RegionId = isRegionId(raw)
    ? raw
    : isDynamicRegionSlug(raw)
      ? (raw as RegionId)
      : DEFAULT_REGION;
  return [safe, (v) => setRaw(v)];
}

/**
 * Hat der User die Region schon einmal explizit gewählt? Wird vom
 * FirstLaunchGate genutzt, um den Region-Picker NUR dann zu zeigen wenn
 * noch nie eine Auswahl getroffen wurde — sonst überschreibt jeder
 * App-Start die alte Wahl.
 *
 * Zum Zeitpunkt des Calls noch keinen React-Hook bemühen — der Gate
 * rendert vor dem ersten Hook-Mount. Synchroner localStorage-Read ist OK.
 */
export function hasActiveRegionBeenChosen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(PREF_ACTIVE_REGION);
    // Auch dynamische Korridor-Slugs zählen als "gewählt" — sonst poppt das
    // FirstLaunchGate wieder auf, sobald ein Self-Service-Korridor aktiv ist.
    return isRegionId(raw) || isDynamicRegionSlug(raw);
  } catch {
    return false;
  }
}

/**
 * Synchroner Setter — vom FirstLaunchGate genutzt, der vor dem
 * useActiveRegion-Hook entscheidet welche Files er herunterlädt.
 * Stellt sicher, dass spätere Hook-Reads den frischen Wert bekommen
 * (usePreference horcht auf 'adt:pref-change' + storage-Events).
 */
export function setActiveRegionSync(region: RegionId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREF_ACTIVE_REGION, region);
    // usePreference horcht auf dieses Event und re-rendert subscribed
    // Komponenten. Ohne das würde useActiveRegion auf den alten Wert
    // klemmen bis ein anderer Setter das Event sendet.
    window.dispatchEvent(
      new CustomEvent('adt:pref-change', {
        detail: { key: PREF_ACTIVE_REGION, value: region },
      }),
    );
  } catch {
    /* ignore — localStorage nicht verfügbar (Private-Mode etc.) */
  }
}

// ---------------------------------------------------------------------------
// Difficulty-Filter: REMOVED (Batch 2, User-Feedback)
// ---------------------------------------------------------------------------
//
// Der Regenbogen unten rechts war nie als Filter gedacht: User klarstellend
// "ein klick darauf soll gar nicht je farbe aktiveren, war nie der wunsch,
// es soll sich eine legende öffnen, die die schwierigkeit der farben
// erklärt." → siehe DifficultyLegend.tsx. Der alte useDifficultyFilter-Hook
// und die PREF_DIFFICULTY_FILTER-Präferenz wurden entfernt; alte Werte im
// localStorage werden ignoriert (harmlos, kein Migrations-Code nötig).
